import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type VideoRecord } from '../lib/api';
import { AudioPreview } from '../components/AudioPreview';

function thumbKeyToShortLabel(key: string): string {
  // Keys look like videos/<id>/thumbnails/concept_1.png — show the trailing label.
  const m = key.match(/concept_(\d+)/);
  if (m) return `Concept ${m[1]}`;
  const parts = key.split('/');
  return parts[parts.length - 1] ?? key;
}

export function VideoApprovalPage() {
  const queryClient = useQueryClient();
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [selectedThumbnailKey, setSelectedThumbnailKey] = useState<string>('');
  const [publishAt, setPublishAt] = useState<string>(''); // datetime-local ('' = publish now/unlisted)
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const queueQuery = useQuery({
    queryKey: ['videos-awaiting'],
    queryFn: api.listVideosAwaitingApproval,
    refetchInterval: 5000,
  });

  const detailQuery = useQuery({
    queryKey: ['video', selectedVideoId],
    queryFn: () => api.getVideo(selectedVideoId!),
    enabled: !!selectedVideoId,
  });

  // Reset selections whenever a new video loads.
  useEffect(() => {
    if (!detailQuery.data) return;
    if (!selectedTitle && detailQuery.data.selected_title) {
      setSelectedTitle(detailQuery.data.selected_title);
    } else if (!selectedTitle && detailQuery.data.title_options?.[0]) {
      setSelectedTitle(detailQuery.data.title_options[0]);
    }
    if (!selectedThumbnailKey && detailQuery.data.selected_thumbnail_key) {
      setSelectedThumbnailKey(detailQuery.data.selected_thumbnail_key);
    } else if (
      !selectedThumbnailKey &&
      detailQuery.data.thumbnail_keys &&
      Object.values(detailQuery.data.thumbnail_keys).length > 0
    ) {
      setSelectedThumbnailKey(Object.values(detailQuery.data.thumbnail_keys)[0]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQuery.data]);

  const approveMutation = useMutation({
    mutationFn: api.approveVideo,
    onSuccess: () => {
      setStatusMsg({ kind: 'ok', text: 'Approved. YouTube upload starting.' });
      setSelectedVideoId(null);
      setSelectedTitle('');
      setSelectedThumbnailKey('');
      setPublishAt('');
      queryClient.invalidateQueries({ queryKey: ['videos-awaiting'] });
    },
    onError: (err: Error) => setStatusMsg({ kind: 'err', text: err.message }),
  });

  const rejectMutation = useMutation({
    mutationFn: api.rejectVideo,
    onSuccess: () => {
      setStatusMsg({ kind: 'ok', text: 'Rejected. Pipeline halted.' });
      setShowRejectInput(false);
      setRejectReason('');
      setSelectedVideoId(null);
      queryClient.invalidateQueries({ queryKey: ['videos-awaiting'] });
    },
    onError: (err: Error) => setStatusMsg({ kind: 'err', text: err.message }),
  });

  const onApprove = () => {
    if (!detailQuery.data?.video_approval_task_token) return;
    if (!selectedTitle || !selectedThumbnailKey) return;
    const publish_at_ms = publishAt ? new Date(publishAt).getTime() : null;
    approveMutation.mutate({
      task_token: detailQuery.data.video_approval_task_token,
      video_id: detailQuery.data.video_id,
      selected_title: selectedTitle,
      selected_thumbnail_key: selectedThumbnailKey,
      publish_at_ms,
    });
  };

  const onReject = () => {
    if (!detailQuery.data?.video_approval_task_token || !rejectReason.trim()) return;
    rejectMutation.mutate({
      task_token: detailQuery.data.video_approval_task_token,
      reason: rejectReason.trim(),
    });
  };

  return (
    <div className="max-w-6xl">
      <h2 className="text-3xl font-bold mb-2">Video Approval Queue</h2>
      <p className="text-parchment-700 mb-6">
        Human-in-the-loop gate #2. Preview the rendered video, pick title + thumbnail,
        set publish time, then approve to upload.
      </p>

      {statusMsg && (
        <div
          className={`rounded-button px-4 py-2 mb-6 text-sm ${
            statusMsg.kind === 'ok'
              ? 'bg-forest-100 text-forest-800'
              : 'bg-red-100 text-red-900'
          }`}
        >
          {statusMsg.text}
        </div>
      )}

      {!selectedVideoId && (
        <>
          {queueQuery.isError && (
            <p className="text-sm font-mono text-red-700 mb-4">api unreachable</p>
          )}
          {queueQuery.data && queueQuery.data.items.length === 0 && (
            <div className="card-neumorphic text-center text-parchment-700 py-12">
              <p className="font-mono text-sm">no videos awaiting approval</p>
            </div>
          )}
          {queueQuery.data && queueQuery.data.items.length > 0 && (
            <ul className="space-y-2">
              {queueQuery.data.items.map((v: VideoRecord) => (
                <li
                  key={v.video_id}
                  className="card-neumorphic !p-4 cursor-pointer hover:shadow-neumorphic-md transition-shadow"
                  onClick={() => setSelectedVideoId(v.video_id)}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="font-medium">
                        {v.selected_title ?? v.title_options?.[0] ?? '(no title yet)'}
                      </p>
                      <p className="text-xs font-mono text-parchment-700 mt-1">
                        {v.video_id} · status: {v.status}
                      </p>
                    </div>
                    <span className="text-xs font-mono rounded-button px-2 py-1 bg-amber-100 text-amber-900">
                      awaiting
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {selectedVideoId && (
        <div>
          <button
            onClick={() => {
              setSelectedVideoId(null);
              setSelectedTitle('');
              setSelectedThumbnailKey('');
              setShowRejectInput(false);
            }}
            className="text-sm font-mono text-forest-700 hover:underline mb-4"
          >
            ← back to queue
          </button>

          {detailQuery.isLoading && (
            <p className="text-sm font-mono text-parchment-700">loading video…</p>
          )}
          {detailQuery.isError && (
            <p className="text-sm font-mono text-red-700">
              video load failed: {detailQuery.error?.message}
            </p>
          )}

          {detailQuery.data && (
            <>
              {detailQuery.data.final_url ? (
                <div className="card-neumorphic mb-6">
                  <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">
                    Rendered Video
                  </h4>
                  <video
                    controls
                    src={detailQuery.data.final_url}
                    className="w-full rounded-button bg-black"
                    style={{ maxHeight: '60vh' }}
                  />
                </div>
              ) : (
                <div className="card-neumorphic mb-6">
                  <p className="font-mono text-xs uppercase text-parchment-700 mb-3">
                    Narration Preview
                  </p>
                  <p className="text-xs font-mono text-parchment-700 mb-3">
                    Final render not yet available — preview narration audio only.
                  </p>
                  <AudioPreview videoId={detailQuery.data.video_id} />
                </div>
              )}

              <div className="card-neumorphic mb-6">
                <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">
                  Pick Title
                </h4>
                <div className="space-y-2">
                  {(detailQuery.data.title_options ?? []).map((title, i) => (
                    <label
                      key={i}
                      className="flex items-start gap-3 cursor-pointer hover:bg-parchment-100 rounded-button p-2 transition-colors"
                    >
                      <input
                        type="radio"
                        name="title"
                        value={title}
                        checked={selectedTitle === title}
                        onChange={() => setSelectedTitle(title)}
                        className="mt-1"
                      />
                      <span className="text-sm">{title}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="card-neumorphic mb-6">
                <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">
                  Pick Thumbnail
                </h4>
                {detailQuery.data.thumbnail_urls &&
                Object.keys(detailQuery.data.thumbnail_urls).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(detailQuery.data.thumbnail_urls).map(([key, url]) => {
                      const s3key = detailQuery.data!.thumbnail_keys?.[key] ?? key;
                      return (
                        <label
                          key={key}
                          className={`cursor-pointer rounded-button overflow-hidden border-2 transition ${
                            selectedThumbnailKey === s3key
                              ? 'border-forest-600'
                              : 'border-transparent'
                          }`}
                        >
                          <input
                            type="radio"
                            name="thumbnail"
                            value={s3key}
                            checked={selectedThumbnailKey === s3key}
                            onChange={() => setSelectedThumbnailKey(s3key)}
                            className="sr-only"
                          />
                          <img src={url} alt={thumbKeyToShortLabel(s3key)} className="w-full" />
                          <p className="text-xs font-mono text-parchment-700 text-center py-2">
                            {thumbKeyToShortLabel(s3key)}
                          </p>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs font-mono text-parchment-700">
                    thumbnails not yet generated
                  </p>
                )}
              </div>

              <div className="card-neumorphic mb-6">
                <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">
                  Publish Time
                </h4>
                <input
                  type="datetime-local"
                  value={publishAt}
                  onChange={(e) => setPublishAt(e.target.value)}
                  className="rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-mono text-sm focus:outline-none focus:ring-2 focus:ring-forest-400"
                />
                <p className="text-xs font-mono text-parchment-700 mt-2">
                  Leave blank to upload as unlisted (manual flip to public later).
                </p>
              </div>

              {!showRejectInput ? (
                <div className="flex gap-3">
                  <button
                    onClick={onApprove}
                    disabled={
                      !selectedTitle ||
                      !selectedThumbnailKey ||
                      !detailQuery.data.video_approval_task_token ||
                      approveMutation.isPending
                    }
                    className="btn-primary disabled:opacity-50"
                  >
                    {approveMutation.isPending ? 'Approving…' : 'Approve & Upload'}
                  </button>
                  <button
                    onClick={() => setShowRejectInput(true)}
                    className="px-4 py-2 rounded-button bg-parchment-50 text-parchment-800 shadow-neumorphic-sm hover:shadow-neumorphic-md transition-shadow"
                  >
                    Reject (partial re-render)
                  </button>
                </div>
              ) : (
                <div className="card-neumorphic">
                  <label className="block text-sm font-medium text-parchment-800 mb-2">
                    Reject reason
                  </label>
                  <textarea
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. b-roll at 2:34 doesn't match the script — needs re-render"
                    className="w-full rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-body focus:outline-none focus:ring-2 focus:ring-forest-400"
                  />
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={onReject}
                      disabled={!rejectReason.trim() || rejectMutation.isPending}
                      className="btn-primary disabled:opacity-50 !bg-red-600 hover:!bg-red-700"
                    >
                      {rejectMutation.isPending ? 'Sending…' : 'Reject'}
                    </button>
                    <button
                      onClick={() => {
                        setShowRejectInput(false);
                        setRejectReason('');
                      }}
                      className="px-4 py-2 rounded-button bg-parchment-50 text-parchment-800 shadow-neumorphic-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
